import { type DragEventHandler, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import type { Directory, SelectedFile } from '@witmetrics/utils';
import {
  useActivePractice,
  useDispatch,
  useFileCollection,
} from '@witmetrics/data-store';
import { parseDroppedItems } from '@witmetrics/utils/files';
import { filterOutKeyValue } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useArray } from '@/hooks/useArray';
import { useMountEffect } from '@/hooks/useMountEffect';
import { stopDefault } from '@/utils/dom';
import { uploadDirectoryTree } from '@/utils/files';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import ProgressBar from '@/components/ProgressBar';
import Card from '@/components/Card';
import Title from '@/components/Title';
import DirectoryInput from '@/components/inputs/DirectoryInput';
import FileInput from '@/components/inputs/FileInput';
import FilePreviewDialog from '@/dialogs/FilePreviewDialog';
import { UploadIcon } from '@/icons';
import StagedEntryRow from './StagedEntryRow';
import type { StagedEntry } from './types';
import {
  buildPreviewFile,
  buildUploadTree,
  classes,
  countFiles,
  countStagedFiles,
  generateEntryPreview,
  removeAtPath,
  revokeEntryPreviews,
  stageDirectory,
  stageFiles,
  styles,
} from './utils';

export type UploadStagingDialogProps = {
  parentFileCollectionId?: number;
  onUploadFileCollections?: (fileCollectionIds: number[]) => void;
  onUploadFiles?: (fileIds: number[]) => void;
  onClose: () => void;
};

export default function UploadStagingDialog({
  parentFileCollectionId,
  onUploadFileCollections,
  onUploadFiles,
  onClose,
}: UploadStagingDialogProps) {
  const directoryInputRef = useRef<HTMLInputElement>(null);
  const entriesRef = useRef<StagedEntry[]>([]);
  const dispatch = useDispatch();
  const { onApiError, onToggleUnloadWarning } = useAppState();
  const activePractice = useActivePractice();
  const parentCollection = useFileCollection(parentFileCollectionId);
  const [entries, setEntries, { addItems, clear }] = useArray<StagedEntry>(
    [],
    true
  );
  const [progress, setProgress] = useState(0);
  const [previewFile, setPreviewFile] = useState<SelectedFile | undefined>();
  const [isUploading, toggleUploading] = useToggle();
  const [isDragging, toggleDragging] = useToggle();

  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  useEffect(() => {
    // Release any generated preview object URLs when the dialog unmounts.
    return () => revokeEntryPreviews(entriesRef.current);
  }, []);

  useMountEffect(() => {
    // eslint-disable-next-line witmetrics/no-multiline-comments
    /* Auto-open the folder picker on mount so a single-folder upload doesn't
      need an extra "Add folders" click. The user can cancel it and still
      drag-and-drop or use the Add buttons.
     */
    directoryInputRef.current?.click();
  });

  const handleAddDirectory = (directory: Directory) => {
    const staged = stageDirectory(directory);
    addItems(staged);
    generatePreviews(staged);
  };

  const handleAddFiles = (files: File[]) => {
    const staged = stageFiles(files);
    addItems(staged);
    generatePreviews(staged);
  };

  const handleRemove = (entry: StagedEntry) => {
    revokeEntryPreviews([entry]);
    setEntries((current) => filterOutKeyValue(current, 'uuid', entry.uuid));
  };

  const handleClearAll = () => {
    revokeEntryPreviews(entries);
    clear();
  };

  const handleRemoveNested = (entryUuid: string, path: string[]) => {
    setEntries((current) =>
      current.flatMap((entry) => {
        if (entry.uuid !== entryUuid || entry.contents instanceof File) {
          return [entry];
        }
        const contents = removeAtPath(entry.contents, path);
        // Drop the whole entry once its folder has been emptied out.
        if (isEmpty(contents)) return [];
        return [{ ...entry, contents, fileCount: countFiles(contents) }];
      })
    );
  };

  const handlePreview = (file: File) => {
    setPreviewFile(buildPreviewFile(file));
  };

  const handleClosePreview = () => {
    if (previewFile) URL.revokeObjectURL(previewFile.source);
    setPreviewFile(undefined);
  };

  const generatePreviews = (staged: StagedEntry[]) => {
    staged.forEach((entry) => {
      if (entry.contents instanceof File) {
        applyPreview(entry.uuid, entry.contents);
      }
    });
  };

  const applyPreview = async (uuid: string, file: File) => {
    try {
      const previewSource = await generateEntryPreview(file);
      if (!previewSource) return;
      setEntries((current) =>
        current.map((entry) =>
          entry.uuid === uuid ? { ...entry, previewSource } : entry
        )
      );
    } catch {
      // A file we can't render keeps its icon — never block staging on it.
    }
  };

  const handleParseDrop = async (items: DataTransferItemList) => {
    try {
      handleAddDirectory(await parseDroppedItems(items));
    } catch (err) {
      onApiError(err, 'Unable to read the dropped items');
    }
  };

  const handleUpload = async () => {
    if (isEmpty(entries)) return;
    const total = countStagedFiles(entries);
    const topLevelFileIds: number[] = [];
    const topLevelFileCollectionIds: number[] = [];
    let uploaded = 0;
    setProgress(0);
    toggleUploading(true);
    onToggleUnloadWarning(true);
    await uploadDirectoryTree(
      buildUploadTree(entries),
      activePractice!.id,
      parentFileCollectionId,
      {
        onFileUploaded: (
          { file, fileCollectionFile, fileVersion },
          isTopLevel
        ) => {
          dispatch.addFiles([file]);
          dispatch.addFileCollectionFiles([fileCollectionFile]);
          dispatch.addFileVersions([fileVersion]);
          if (isTopLevel) topLevelFileIds.push(file.id);
          uploaded += 1;
          setProgress(total === 0 ? 100 : Math.round((uploaded / total) * 100));
        },
        onCollectionCreated: (fileCollection, isTopLevel) => {
          dispatch.addFileCollections([fileCollection]);
          if (isTopLevel) topLevelFileCollectionIds.push(fileCollection.id);
        },
        onError: (err) => onApiError(err, 'Error uploading file(s)'),
      }
    );
    onToggleUnloadWarning(false);
    toggleUploading(false);
    if (!isEmpty(topLevelFileIds)) onUploadFiles?.(topLevelFileIds);
    if (!isEmpty(topLevelFileCollectionIds)) {
      onUploadFileCollections?.(topLevelFileCollectionIds);
    }
    onClose();
  };

  const handleDragOver: DragEventHandler<HTMLDivElement> = stopDefault(() => {
    if (!isDragging) toggleDragging(true);
  });

  const handleDragLeave: DragEventHandler<HTMLDivElement> = stopDefault(() => {
    toggleDragging(false);
  });

  const handleDrop: DragEventHandler<HTMLDivElement> = stopDefault((e) => {
    toggleDragging(false);
    handleParseDrop(e.dataTransfer.items);
  });

  const title = parentCollection
    ? `Upload to "${parentCollection.name}"`
    : 'Upload';

  return (
    <>
      <Dialog
        className={twMerge(
          classes.dialog,
          !isEmpty(entries) && classes.dialogWide
        )}
        onClose={onClose}>
        <DialogTitle title={title} onClose={onClose} />
        <DialogBody className={classes.body}>
          <DirectoryInput
            ref={directoryInputRef}
            className={classes.hiddenInput}
            id="staging-directories"
            onSelect={handleAddDirectory}
          />
          {!!parentFileCollectionId && (
            <FileInput
              multiple
              className={classes.hiddenInput}
              id="staging-files"
              onSelect={handleAddFiles}
            />
          )}
          <div className={classes.columns}>
            <div
              className={twMerge(
                classes.inputColumn,
                !isEmpty(entries) && classes.inputColumnSplit
              )}>
              <label htmlFor="staging-directories">
                <Card
                  sx={styles.dropCard}
                  className={twMerge(
                    classes.dropCard,
                    isDragging && classes.dropCardActive
                  )}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}>
                  <div className={classes.dropContent}>
                    <div className={classes.dropIconWrapper}>
                      <UploadIcon />
                    </div>
                    <Title className={classes.dropTitle} size="xs">
                      Add folders
                    </Title>
                    <div className={classes.dropSubtitle}>
                      Drag &amp; drop or click to browse
                    </div>
                  </div>
                </Card>
              </label>
              {!!parentFileCollectionId && (
                <div className={classes.addFilesWrapper}>
                  <span className={classes.addFilesOr}>or</span>
                  <label
                    className={classes.addFilesLabel}
                    htmlFor="staging-files">
                    add individual files
                  </label>
                </div>
              )}
            </div>
            {!isEmpty(entries) && (
              <div className={classes.listColumn}>
                <div className={classes.listHeader}>
                  <span>Selected ({entries.length})</span>
                  <button
                    className={classes.clearButton}
                    type="button"
                    onClick={handleClearAll}>
                    Clear all
                  </button>
                </div>
                <div className={classes.list}>
                  {entries.map((entry) => (
                    <StagedEntryRow
                      key={entry.uuid}
                      entry={entry}
                      onPreview={handlePreview}
                      onRemove={isUploading ? undefined : handleRemove}
                      onRemoveNested={
                        isUploading ? undefined : handleRemoveNested
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          {isUploading && (
            <ProgressBar className={classes.progress} progress={progress} />
          )}
        </DialogBody>
        <DefaultDialogActions
          disabled={isEmpty(entries)}
          pending={isUploading}
          confirmLabel={`Upload ${entries.length} item${entries.length === 1 ? '' : 's'}`}
          pendingLabel="Uploading"
          confirmIcon={<UploadIcon />}
          onCancel={onClose}
          onConfirm={handleUpload}
        />
      </Dialog>
      {previewFile && (
        <FilePreviewDialog
          selectedFile={previewFile}
          onClose={handleClosePreview}
        />
      )}
    </>
  );
}
